import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/class-control"
import { Calendar } from "@/components/ui/calendar"
import { ControllerRenderProps, FieldValues } from "react-hook-form"
import { SyntheticEvent } from "react"

const Popover = PopoverPrimitive.Root
const PopoverTrigger = PopoverPrimitive.Trigger
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "start", sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border-0.5 border-gray-600 bg-gray-800 p-0 text-white shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

interface DatePickerProps<T extends FieldValues> {
  field: ControllerRenderProps<T>
  placeholder?: string
  className?: string
  disabled?: boolean
  disabledPeriod?: (date: Date) => boolean
}

export default function DatePicker<T extends FieldValues>({
  field,
  placeholder = "Selecione uma data",
  className,
  disabled,
  disabledPeriod,
  ...props
}: DatePickerProps<T>) {
  const [open, setOpen] = React.useState(false)

  const toggleOpen = () => setOpen(disabled ? false : !open)

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return ""

    // Handle string dates from API
    const dateObj = typeof date === 'string' ? new Date(date) : date

    // Validate it's a valid date
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return ""
    }

    return dateObj.toLocaleDateString("pt-BR")
  }

  const handleSelect = (date: SyntheticEvent<HTMLDivElement, Event> | Date | Date[] | { from: Date; to?: Date | undefined; } | undefined) => {
    field.onChange(date || null)
    setOpen(false)
  }

  // Convert string dates to Date objects for Calendar component
  const getDateValue = () => {
    if (!field.value) return undefined
    if (field.value && (field.value as any) instanceof Date) return field.value
    if (typeof field.value === 'string') {
      const date = new Date(field.value)
      return isNaN(date.getTime()) ? undefined : date
    }
    return undefined
  }

  return (
    <Popover open={open} onOpenChange={toggleOpen}>
      <PopoverTrigger asChild>
        <div className={cn("flex items-center w-full px-input-x py-input-y border-0.5 border-gray-600 rounded-lg",
          "bg-[#15203c] text-white focus-within:ring-1 focus-within:ring-primary focus-within:ring-offset-0",
          "cursor-pointer", disabled && "opacity-50 bg-[#0c1427] cursor-not-allowed")}>
          <input
            name={field.name}
            ref={field.ref}
            value={formatDate(field.value)}
            placeholder={placeholder}
            readOnly
            className={cn(
              "flex-1 bg-transparent outline-none text-sm text-white placeholder-gray-400 cursor-pointer",
              disabled && "cursor-not-allowed",
              className
            )}
            disabled={disabled}
            {...props}
          />
          <CalendarIcon className="h-4 w-4 text-gray-400 ml-2" />
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={getDateValue()}
          onSelect={e => { handleSelect(e) }}
          disabled={disabled}
          disabledPeriod={disabledPeriod}
        />
      </PopoverContent>
    </Popover>
  )
}

DatePicker.displayName = "DatePicker"